import { type FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { cx } from "@emotion/css";
import { Box, Flex } from "@mantine/core";
import { Alert, Status } from "@snapchat/snap-design-system";
import { useRecoilValue } from "recoil";

import { useAssistantLensDetailsContext } from "../../../assistant-chat/hooks";
import { UnsavedChangesPrompt } from "../../../camkit/common/components";
import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, LensCreatorPayoutEligibility, SurfaceType, Visibility } from "../../../gql-generated/types";
import { HostedLensSection } from "../../../hosted-lenses/components/HostedLensSection";
import { Card } from "../../../layout/components";
import { LensCloudSection } from "../../../lens-cloud/components/LensCloudSection";
import { LensStats } from "../../../lens-stats/components";
import { Flexbox } from "../../../shared/components";
import { LensAutomationToolAlert } from "../../../shared/components/LensAutomationToolAlert";
import { ROLE_ACTIONS } from "../../../shared/constants";
import { useToggleState } from "../../../shared/hooks";
import { usePermittedBasedOnLensFolderRoleAction } from "../../../shared/hooks/roles";
import { defaultPreviewMediaItem, lensPreviewMediaItemsAtomById } from "../../../shared/state";
import { type LensInfo } from "../../api/queries";
import {
    useGameLensState,
    useLensActionsState,
    useLensChangeTracker,
    useLensDestinations,
    useLensStatusPanel,
} from "../../hooks";
import { getLensScanCode } from "../../utils/getLensScanCode";
import { LensDistributionRestrictionsModal } from "../shared";

import { LensCreatorRewardsSection } from "./LensCreatorRewardsSection";
import { LensDetailsSection } from "./LensDetailsSection";
import { LensDistributionRestrictionsWarning } from "./LensDistributionRestrictionsWarning";
import { LensHeader } from "./LensHeader";
import { LensSidebar } from "./LensSidebar";
import { lensDetailsContainer, sectionSpacing } from "./LensSourcePage.styles";
import { LensStatusPanel } from "./LensStatusPanel";
import { UnsavedChangesActionText, UnsavedChangesCancelText } from "./messages.generated";
import { RejectionErrorsPanelV2 } from "./rejection-errors";

const BREAKPOINT_SIDEBAR = "lg";

export const LensSourcePage: FC<LensInfo> = ({
    id,
    iconUrl,
    name,
    status,
    visibility,
    updatedAt,
    createdAt,
    qrCode,
    lensAccountId,
    tagsList,
    spectaclesTags,
    digitalGoodsList,
    primaryCategoryId,
    secondaryCategoryId,
    rejectionErrors,
    discoverability,
    distributionsList,
    distributionRestrictionsList,
    lensCreatorPayoutEligibility,
    disabledDestinationTypesList,
    unlockableId,
    surfaceType,
    sourceApplication,
    previewMediaList,
    exclusiveLensStatus,
    markedAsExclusiveAt,
    isGameUserProvided,
}) => {
    const lensPreviewMediaItems = useRecoilValue(lensPreviewMediaItemsAtomById(id));
    const [isGameLensPreviewSaveDisabled, setIsGameLensPreviewSaveDisabled] = useState(false);

    const {
        updateCreatorPayout,
        creatorRewardProgramEnrolled,
        profileVisibility,
        tags: editedTags,
        hasChanges,
        hasFieldChanged,
    } = useLensChangeTracker(id);

    const { orgId } = useParams();
    const { isPermitted: isPermittedToViewInsights } = usePermittedBasedOnLensFolderRoleAction(
        lensAccountId?.lensSpaceId ?? "",
        ROLE_ACTIONS.READ_LENS_INSIGHTS,
    );

    const scanCode = useMemo(() => getLensScanCode(qrCode), [qrCode]);

    const { configs } = useFeatureGates();
    const isHostedLensesEnabled = configs[CofConfigId.MyLensesHostedLenses].enabled;
    const isLensCloudEnabled = configs[CofConfigId.MyLensesLensCloudProvisioning].enabled;

    const { destinations, loading: destinationsLoading } = useLensDestinations(
        id,
        lensAccountId?.lensSpaceId ?? undefined,
        distributionsList,
    );

    const {
        actionsState: { actionsConfig, lensActionsErrorMap },
    } = useLensActionsState(
        {
            status,
            qrCode,
            digitalGoodsList,
            rejectionErrors,
            distributionsList,
            lensAccountId,
            sourceApplication,
        },
        destinations,
    );

    const [restrictionModalProps, showRestrictionModal] = useToggleState();

    const lensStatusPanelProps = useLensStatusPanel(
        status,
        id,
        name,
        iconUrl,
        destinations,
        distributionRestrictionsList,
        destinationsLoading,
        rejectionErrors ?? undefined,
        actionsConfig,
        showRestrictionModal,
        surfaceType,
        exclusiveLensStatus,
        lensAccountId?.lensSpaceId ?? undefined,
    );

    const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const hasSpectacles = surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const gameLensState = useGameLensState({
        primaryCategoryId,
        isGameUserProvided,
    });

    const displayVisibility = profileVisibility ?? visibility;
    const haveAssistantTagsChanged = hasFieldChanged("tags");
    const assistantLensCreatorPayoutEligibility = hasFieldChanged("creatorRewardProgramEnrolled")
        ? creatorRewardProgramEnrolled
            ? LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible
            : LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset
        : lensCreatorPayoutEligibility;
    const assistantLensDetailsInput = useMemo(
        () => ({
            lensKey: id,
            name,
            publicationStatus: lensStatusPanelProps.title,
            displayVisibility,
            exclusiveLensStatus,
            lensCreatorPayoutEligibility: assistantLensCreatorPayoutEligibility,
            surfaceTypes: surfaceType,
            sourceApplication,
            createdAt,
            updatedAt,
            tags: haveAssistantTagsChanged ? editedTags ?? [] : tagsList,
        }),
        [
            id,
            name,
            lensStatusPanelProps.title,
            displayVisibility,
            exclusiveLensStatus,
            assistantLensCreatorPayoutEligibility,
            surfaceType,
            sourceApplication,
            createdAt,
            updatedAt,
            haveAssistantTagsChanged,
            editedTags,
            tagsList,
        ],
    );
    useAssistantLensDetailsContext(assistantLensDetailsInput);

    return (
        <Flexbox flexDirection="column">
            {!isSpectaclesOnly && <LensAutomationToolAlert />}
            <LensHeader
                orgId={orgId ?? ""}
                lensId={id}
                lensName={name}
                unlockableId={unlockableId}
                lensCreatorPayoutEligibility={lensCreatorPayoutEligibility}
                status={status}
                isSpectaclesOnly={isSpectaclesOnly}
                deleteLensPermission={{
                    isPermitted: actionsConfig.deleteLens,
                    reason: lensActionsErrorMap.deleteLens,
                }}
                disableGenerateWebAR={!actionsConfig.generateHostedWebAr}
                deeplink={qrCode?.deeplink}
                isSaveDisabled={isGameLensPreviewSaveDisabled}
            />
            <RejectionErrorsPanelV2 lensId={id} className={sectionSpacing} />

            {hasChanges && (
                <Box mb="xs">
                    <Alert
                        status={Status.WARNING}
                        title="Unsaved Changes"
                        message="You have unsaved changes. Save or discard your changes to remove this warning."
                    />
                </Box>
            )}

            {distributionRestrictionsList.length > 0 && (
                <LensDistributionRestrictionsWarning className={sectionSpacing} onActionClick={showRestrictionModal} />
            )}
            <Flex className={sectionSpacing} direction={{ base: "column", [BREAKPOINT_SIDEBAR]: "row" }} gap="md">
                <Flexbox flexDirection="column" data-testid="lens-details-section" className={lensDetailsContainer}>
                    <Card className={cx(sectionSpacing)}>
                        <LensStatusPanel
                            {...lensStatusPanelProps}
                            visibility={displayVisibility}
                            isSpectaclesOnly={isSpectaclesOnly}
                        />
                    </Card>
                    <Card>
                        <LensDetailsSection
                            iconUrl={iconUrl}
                            name={name}
                            status={status}
                            updatedAt={updatedAt ?? undefined}
                            primaryCategoryId={primaryCategoryId}
                            secondaryCategoryId={secondaryCategoryId}
                            previewMediaList={previewMediaList ?? []}
                            lensId={id}
                            actionsConfig={actionsConfig}
                            disabledDestinationTypesList={disabledDestinationTypesList}
                            surfaceType={surfaceType}
                            scanCode={scanCode}
                            shouldValidateGameLensPreview={gameLensState !== null}
                            onGameLensPreviewSaveDisabledChange={setIsGameLensPreviewSaveDisabled}
                        />
                    </Card>
                </Flexbox>

                <LensSidebar
                    tags={tagsList}
                    spectaclesTags={spectaclesTags ?? []}
                    status={status}
                    lensIconURL={iconUrl}
                    lensPreview={lensPreviewMediaItems[0] ?? defaultPreviewMediaItem}
                    lensId={id}
                    discoverability={discoverability ?? null}
                    actionsConfig={actionsConfig}
                    primaryCategoryId={primaryCategoryId}
                    isGameUserProvided={isGameUserProvided}
                    isSpectaclesOnly={isSpectaclesOnly}
                    hasSpectacles={hasSpectacles}
                />
            </Flex>
            <Flex gap="lg" direction="column">
                {isLensCloudEnabled && <LensCloudSection lensId={id} />}

                {!isSpectaclesOnly && (
                    <LensCreatorRewardsSection
                        lensId={id}
                        lensName={name}
                        unlockableId={unlockableId}
                        lensCreatorPayoutEligibility={lensCreatorPayoutEligibility}
                        distributionsList={distributionsList}
                        createdAt={createdAt ?? undefined}
                        markedAsExclusiveAt={markedAsExclusiveAt}
                        previewMediaItems={lensPreviewMediaItems}
                        exclusiveLensStatus={exclusiveLensStatus}
                        visibility={visibility}
                        actionsDisabled={
                            visibility === Visibility.VisibilityDontPublishToSnapchat ||
                            actionsConfig.enrollCreatorPayout.disabled
                        }
                        updateCreatorPayout={updateCreatorPayout}
                        creatorRewardProgramEnrolled={creatorRewardProgramEnrolled}
                    />
                )}
                {isHostedLensesEnabled && !isSpectaclesOnly && (
                    <HostedLensSection
                        lensID={id}
                        organizationId={orgId ?? ""}
                        unlockableId={unlockableId}
                        disableDelete={!actionsConfig.deleteHostedWebAr}
                        disableGenerate={!actionsConfig.generateHostedWebAr}
                    />
                )}
                {isPermittedToViewInsights && !isSpectaclesOnly && (
                    <LensStats
                        lensId={id}
                        unlockableId={unlockableId.toString()}
                        exclusiveLensStatus={exclusiveLensStatus}
                        markedAsExclusiveAt={markedAsExclusiveAt}
                    />
                )}
            </Flex>
            <LensDistributionRestrictionsModal lensId={id} lensName={name} modalProps={restrictionModalProps} />
            <UnsavedChangesPrompt
                when={hasChanges}
                cancelText={<UnsavedChangesCancelText />}
                actionText={<UnsavedChangesActionText />}
            />
        </Flexbox>
    );
};
